// web/src/pages/statements/BulkStatementRunWizard.tsx
//
// I-8 — 4-step "Bulk statement run" wizard (Plan III Task 22).
//
// Step 1: Select accounts (filter inputs)
// Step 2: Choose delivery mode
// Step 3: Preview (count-only — spec sample-render dropped; see deviation flag below)
// Step 4: Confirm + run
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { PageContainer } from "@/app/layouts/PageContainer";
import {
  createStatementRun,
  type CreateStatementRunRequest,
} from "@/lib/invoices-payments";

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const Schema = z.object({
  onlyHasOpenBalance: z.boolean(),
  branchIds: z.string(),                   // CSV → number[]
  termsIds: z.string(),                    // CSV → number[]
  deliveryMethodIds: z.string(),           // CSV → number[]
  deliveryMode: z.enum(["Email", "Print", "Both"]),
  notes: z.string().max(2000).optional(),
});
type FormValues = z.infer<typeof Schema>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const csvToNumbers = (s: string): number[] =>
  s
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean)
    .map(Number)
    .filter((n) => Number.isFinite(n));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BulkStatementRunWizard() {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: {
      onlyHasOpenBalance: true,
      branchIds: "",
      termsIds: "",
      deliveryMethodIds: "",
      deliveryMode: "Email",
      notes: "",
    },
  });

  const createMut = useMutation({
    mutationFn: (req: CreateStatementRunRequest) => createStatementRun(req),
    onSuccess: ({ runId }) => navigate(`/statements/runs/${runId}`),
  });

  function handleConfirm(values: FormValues) {
    createMut.mutate({
      deliveryMode: values.deliveryMode,
      notes: values.notes || undefined,
      accountFilter: {
        onlyHasOpenBalance: values.onlyHasOpenBalance,
        branchIds: csvToNumbers(values.branchIds),
        termsIds: csvToNumbers(values.termsIds),
        deliveryMethodIds: csvToNumbers(values.deliveryMethodIds),
      },
    });
  }

  return (
    <PageContainer>
      <h1 className="mb-1 text-2xl font-semibold">New statement run</h1>

      {/* Step indicator */}
      <ol className="mb-6 flex gap-3 text-sm" data-testid="bulk-statement-run-wizard">
        {(["Select accounts", "Delivery mode", "Preview", "Confirm"] as const).map(
          (label, i) => (
            <li
              key={label}
              className={step === i + 1 ? "font-semibold" : "text-muted-foreground"}
            >
              {i + 1}. {label}
            </li>
          ),
        )}
      </ol>

      {/* Step 1 — Select accounts */}
      {step === 1 && (
        <section aria-label="Step 1: Select accounts" className="space-y-4 max-w-xl">
          <p className="text-sm text-muted-foreground">
            step 1 of 4 — choose which accounts to include in this run.
          </p>
          <div className="flex items-center gap-2">
            <Checkbox
              id="onlyOpen"
              checked={form.watch("onlyHasOpenBalance")}
              onCheckedChange={(checked) =>
                form.setValue("onlyHasOpenBalance", checked === true)
              }
            />
            <Label htmlFor="onlyOpen">Only accounts with open balance</Label>
          </div>
          <div>
            <Label htmlFor="branchIds">Branch IDs (comma-separated, optional)</Label>
            <Input
              id="branchIds"
              placeholder="e.g. 1, 2, 3"
              {...form.register("branchIds")}
            />
          </div>
          <div>
            <Label htmlFor="termsIds">Terms IDs (comma-separated, optional)</Label>
            <Input
              id="termsIds"
              placeholder="e.g. 10, 20"
              {...form.register("termsIds")}
            />
          </div>
          <div>
            <Label htmlFor="deliveryMethodIds">
              Delivery method IDs (comma-separated, optional)
            </Label>
            <Input
              id="deliveryMethodIds"
              placeholder="e.g. 5"
              {...form.register("deliveryMethodIds")}
            />
          </div>
          <div className="flex justify-end">
            <Button type="button" onClick={() => setStep(2)}>
              Next
            </Button>
          </div>
        </section>
      )}

      {/* Step 2 — Delivery mode */}
      {step === 2 && (
        <section aria-label="Step 2: Delivery mode" className="space-y-4 max-w-xl">
          <p className="text-sm text-muted-foreground">
            step 2 of 4 — choose how statements will be delivered.
          </p>
          <div>
            <Label>Delivery mode</Label>
            <Select
              value={form.watch("deliveryMode")}
              onValueChange={(v) =>
                form.setValue("deliveryMode", v as FormValues["deliveryMode"])
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Email">Email</SelectItem>
                <SelectItem value="Print">Print</SelectItem>
                <SelectItem value="Both">Both (email + print)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-between">
            <Button type="button" variant="ghost" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button type="button" onClick={() => setStep(3)}>
              Next
            </Button>
          </div>
        </section>
      )}

      {/* Step 3 — Preview */}
      {step === 3 && (
        <section aria-label="Step 3: Preview" className="space-y-4 max-w-xl">
          <p className="text-sm text-muted-foreground">
            step 3 of 4 — review before submitting.
          </p>
          <p className="text-sm text-slate-600">
            Preview: filter will be applied at run time. Per-account row preview is
            deferred — confirm to enqueue and watch live progress on the next page.
          </p>
          {/* TODO (Plan III follow-up): per-account preview + sample-render link */}
          <div className="rounded-md border p-3 text-sm">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
              <dt className="text-muted-foreground">Delivery mode</dt>
              <dd>{form.watch("deliveryMode")}</dd>
              <dt className="text-muted-foreground">Open balance only</dt>
              <dd>{form.watch("onlyHasOpenBalance") ? "Yes" : "No"}</dd>
              <dt className="text-muted-foreground">Branch IDs</dt>
              <dd>{form.watch("branchIds") || "(all)"}</dd>
              <dt className="text-muted-foreground">Terms IDs</dt>
              <dd>{form.watch("termsIds") || "(all)"}</dd>
              <dt className="text-muted-foreground">Delivery method IDs</dt>
              <dd>{form.watch("deliveryMethodIds") || "(all)"}</dd>
            </dl>
          </div>
          <div className="flex justify-between">
            <Button type="button" variant="ghost" onClick={() => setStep(2)}>
              Back
            </Button>
            <Button type="button" onClick={() => setStep(4)}>
              Next
            </Button>
          </div>
        </section>
      )}

      {/* Step 4 — Confirm */}
      {step === 4 && (
        <section aria-label="Step 4: Confirm" className="max-w-xl">
          <p className="mb-4 text-sm text-muted-foreground">
            step 4 of 4 — add optional notes and start the run.
          </p>
          <form className="space-y-4" onSubmit={form.handleSubmit(handleConfirm)}>
            <div>
              <Label htmlFor="notes">Notes (optional)</Label>
              <Input
                id="notes"
                placeholder="e.g. Month-end run May 2026"
                {...form.register("notes")}
              />
            </div>
            {createMut.isError && (
              <p className="text-sm text-destructive">
                Could not enqueue the run. Please retry.
              </p>
            )}
            <div className="flex justify-between">
              <Button type="button" variant="ghost" onClick={() => setStep(3)}>
                Back
              </Button>
              <Button type="submit" disabled={createMut.isPending}>
                {createMut.isPending ? "Enqueuing..." : "Run now"}
              </Button>
            </div>
          </form>
        </section>
      )}
    </PageContainer>
  );
}
